import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  MapPin,
  LocateFixed,
  Star,
  BookOpen,
  ChevronRight,
  Crown,
  Clover,
  Loader2,
  Mountain,
  Plane,
} from 'lucide-react';
import { races } from '../events/catalog';
import { guides } from '../events/guides';
import { daysUntil } from '../RaceCountdown';
import type { Race } from '../events/types';

type Filter = 'upcoming' | 'near' | 'majors';
type Coords = { lat: number; lng: number };

const LOCATION_KEY = 'plan-location';

const distanceKm = (a: Coords, b: Coords): number => {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(h));
};

const formatDate = (iso: string): string =>
  new Intl.DateTimeFormat('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${iso}T12:00:00Z`),
  );

const formatDistance = (km: number): string => (km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km).toLocaleString()} km`);

const readStoredLocation = (): Coords | null => {
  try {
    const raw = localStorage.getItem(LOCATION_KEY);
    return raw ? (JSON.parse(raw) as Coords) : null;
  } catch {
    return null;
  }
};

const RaceCard: React.FC<{
  race: Race;
  selected: boolean;
  distance?: number;
  onSelect: (race: Race) => void;
}> = ({ race, selected, distance, onSelect }) => {
  const days = daysUntil(race.date, race.timezone);
  const hasGuide = !!guides[race.id];
  return (
    <motion.button
      layout
      className={`plan-race ${selected ? 'plan-race--selected' : ''} ${hasGuide ? 'plan-race--guide' : ''}`}
      onClick={() => onSelect(race)}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="plan-race-date">
        <span className="plan-race-day">{race.date.slice(8, 10)}</span>
        <span className="plan-race-month">
          {new Intl.DateTimeFormat('en-AU', { month: 'short', timeZone: 'UTC' }).format(new Date(`${race.date}T12:00:00Z`))}
        </span>
        <span className="plan-race-year">{race.date.slice(0, 4)}</span>
      </div>
      <div className="plan-race-body">
        <div className="plan-race-title-row">
          <h3>{race.name}</h3>
          {race.major && (
            <span className="plan-badge plan-badge--major">
              <Star size={10} /> Major
            </span>
          )}
          {hasGuide && (
            <span className="plan-badge plan-badge--guide">
              <BookOpen size={10} /> Full guide
            </span>
          )}
        </div>
        <p className="plan-race-meta">
          <MapPin size={11} /> {race.city}, {race.country}
          {distance !== undefined && <span className="plan-race-dist"> · {formatDistance(distance)} away</span>}
        </p>
        <p className="plan-race-sub">
          {formatDate(race.date)}
          {!race.dateConfirmed && ' (TBC)'} · {days > 0 ? `${days} days` : days === 0 ? 'today' : 'done'}
          {race.knownFor ? ` · ${race.knownFor}` : ''}
        </p>
      </div>
      <ChevronRight size={18} className="plan-race-chevron" />
    </motion.button>
  );
};

const PlanScreen: React.FC<{
  selectedRaceId: string | null;
  onSelectRace: (race: Race) => void;
  onOpenPastTrip: (trip: 'london' | 'dublin' | 'galway' | 'singapore') => void;
}> = ({ selectedRaceId, onSelectRace, onOpenPastTrip }) => {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('upcoming');
  const [location, setLocation] = useState<Coords | null>(readStoredLocation);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  useEffect(() => {
    if (!location) return;
    try {
      localStorage.setItem(LOCATION_KEY, JSON.stringify(location));
    } catch {
      // Storage unavailable — location will be requested again next visit.
    }
  }, [location]);

  const locate = () => {
    if (!navigator.geolocation) {
      setLocationError('Location isn’t available in this browser — search by city instead.');
      return;
    }
    setLocating(true);
    setLocationError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setFilter('near');
        setLocating(false);
      },
      () => {
        setLocationError('Couldn’t get your location — search by city or country instead.');
        setLocating(false);
      },
      { maximumAge: 1000 * 60 * 60 * 24, timeout: 10000 },
    );
  };

  const selectedRace = races.find((r) => r.id === selectedRaceId) ?? null;

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const upcoming = races.filter((r) => daysUntil(r.date, r.timezone) >= 0);
    const matched = upcoming.filter(
      (r) => !q || [r.name, r.city, r.country].some((field) => field.toLowerCase().includes(q)),
    );
    const withDistance = matched.map((r) => ({ race: r, distance: location ? distanceKm(location, r) : undefined }));
    if (filter === 'majors') return withDistance.filter((x) => x.race.major);
    if (filter === 'near' && location) return [...withDistance].sort((a, b) => a.distance! - b.distance!);
    return withDistance;
  }, [query, filter, location]);

  return (
    <div className="plan-screen">
      <motion.section className="plan-hero" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
        <div className="plan-hero-inner">
          <span className="plan-eyebrow">Maraplan</span>
          <h1>
            Plan your next <em>big race</em>
          </h1>
          <p className="plan-lede">
            Pick a race to get a carb-load plan timed to your wave, a race-week calendar, finisher perks, and a day-by-day
            trip itinerary.
          </p>
          <div className="plan-search">
            <Search size={18} />
            <input
              type="search"
              placeholder="Search races, cities, countries…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search races"
            />
          </div>
        </div>
      </motion.section>

      <div className="plan-body">
        {selectedRace && (
          <section className="plan-block">
            <h2 className="plan-heading">Your race</h2>
            <RaceCard race={selectedRace} selected onSelect={onSelectRace} />
          </section>
        )}

        <section className="plan-block">
          <div className="plan-heading-row">
            <h2 className="plan-heading">{filter === 'near' && location ? 'Races near you' : 'Upcoming races'}</h2>
            <div className="plan-filters">
              <button
                className={`plan-filter ${filter === 'upcoming' ? 'plan-filter--active' : ''}`}
                onClick={() => setFilter('upcoming')}
              >
                By date
              </button>
              <button
                className={`plan-filter ${filter === 'near' ? 'plan-filter--active' : ''}`}
                onClick={() => (location ? setFilter('near') : locate())}
              >
                {locating ? <Loader2 size={12} className="plan-spin" /> : <LocateFixed size={12} />} Near me
              </button>
              <button
                className={`plan-filter ${filter === 'majors' ? 'plan-filter--active' : ''}`}
                onClick={() => setFilter('majors')}
              >
                <Star size={12} /> Majors
              </button>
            </div>
          </div>
          {locationError && <p className="plan-note">{locationError}</p>}
          {filter === 'near' && location && (
            <p className="plan-note">
              Sorted by distance from your location.{' '}
              <button className="plan-link" onClick={locate}>
                Refresh location
              </button>
            </p>
          )}
          <div className="plan-list">
            {results.map(({ race, distance }) => (
              <RaceCard
                key={race.id}
                race={race}
                selected={race.id === selectedRaceId}
                distance={distance}
                onSelect={onSelectRace}
              />
            ))}
            {results.length === 0 && <p className="plan-empty">No upcoming races match “{query}”.</p>}
          </div>
        </section>

        <section className="plan-block">
          <h2 className="plan-heading">Past trips</h2>
          <div className="plan-past">
            <button className="plan-past-card plan-past-card--london" onClick={() => onOpenPastTrip('london')}>
              <Crown size={18} />
              <span>
                <strong>London Marathon 2026</strong>
                <em>26 April · itinerary, fuelling, shopping, perks</em>
              </span>
              <ChevronRight size={16} />
            </button>
            <button className="plan-past-card plan-past-card--dublin" onClick={() => onOpenPastTrip('dublin')}>
              <Clover size={18} />
              <span>
                <strong>Dublin 2026</strong>
                <em>Post-London trip · 5 days</em>
              </span>
              <ChevronRight size={16} />
            </button>
            <button className="plan-past-card plan-past-card--galway" onClick={() => onOpenPastTrip('galway')}>
              <Mountain size={18} />
              <span>
                <strong>Galway 2026</strong>
                <em>West coast roadtrip · 3 days</em>
              </span>
              <ChevronRight size={16} />
            </button>
            <button className="plan-past-card plan-past-card--singapore" onClick={() => onOpenPastTrip('singapore')}>
              <Plane size={18} />
              <span>
                <strong>Singapore 2026</strong>
                <em>Layover on the way home</em>
              </span>
              <ChevronRight size={16} />
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PlanScreen;
